/**
 * Deployment Type Detection and Configuration
 * Determines whether the current build is for SaaS or Enterprise deployment
 */

import environment from '../environmentLoader';

export type DeploymentType = 'saas' | 'enterprise';

export interface DeploymentConfig {
  type: DeploymentType;
  features: {
    // Core features (always available)
    core: {
      networkDiagrams: boolean;
      serverlessDiagrams: boolean;
      accountManagement: boolean;
      errorHandling: boolean;
    };

    // Shared features (configurable)
    shared: {
      multiAccount: boolean;
      regionPreferences: boolean;
      settings: boolean;
      transitGateway: boolean;
    };

    // SaaS-specific features
    saas: {
      subscriptions: boolean;
      stripe: boolean;
      analytics: boolean;
      selfSignup: boolean;
    };

    // Enterprise-specific features
    enterprise: {
      customBranding: boolean;
      ssoIntegration: boolean;
      privateAccess: boolean;
      customDomain: boolean;
      dedicatedSupport: boolean;
    };
  };
}

/**
 * Detects deployment type based on environment configuration
 */
export function detectDeploymentType(): DeploymentType {
  // Check for enterprise indicators
  const isEnterprise =
    !environment.features.subscriptions ||
    !environment.stripe.enabled ||
    !environment.matomo.enabled;

  return isEnterprise ? 'enterprise' : 'saas';
}

/**
 * Gets deployment-specific configuration
 */
export function getDeploymentConfig(): DeploymentConfig {
  const deploymentType = detectDeploymentType();

  const baseConfig: DeploymentConfig = {
    type: deploymentType,
    features: {
      core: {
        networkDiagrams: true,
        serverlessDiagrams: true,
        accountManagement: true,
        errorHandling: true,
      },
      shared: {
        multiAccount: true,
        regionPreferences: true,
        settings: true,
        transitGateway: true,
      },
      saas: {
        subscriptions: false,
        stripe: false,
        analytics: false,
        selfSignup: false,
      },
      enterprise: {
        customBranding: false,
        ssoIntegration: false,
        privateAccess: false,
        customDomain: false,
        dedicatedSupport: false,
      },
    },
  };

  if (deploymentType === 'saas') {
    baseConfig.features.saas = {
      subscriptions: environment.features.subscriptions || false,
      stripe: environment.stripe.enabled || false,
      analytics: environment.matomo.enabled || false,
      selfSignup: environment.cognitoSelfSignUpEnabled || false,
    };
  } else {
    baseConfig.features.enterprise = {
      customBranding: true,
      ssoIntegration: true,
      privateAccess: true,
      customDomain: true,
      dedicatedSupport: true,
    };
  }

  return baseConfig;
}

/**
 * Current deployment configuration
 */
export const deploymentConfig = getDeploymentConfig();